import { State, Action, StateContext, Selector } from '@ngxs/store';
import { GalleryStateModel } from './gallery.state.model';
import { GallerySearchMode } from '../models/gallery-search-mode.enum';
import { GalleryViewMode } from '../models/gallery-view-mode.enum';
import { GalleryActions } from './gallery.actions';
import { exploreMenu } from 'src/app/explore/data/explore-menu';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { of, forkJoin, Observable } from 'rxjs';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { StudioOptionsModel } from '../models/studio-options.model';
import { ModulesService } from 'src/app/modules/modules.service';
import { finalize, filter, map } from 'rxjs/operators';
import { ExploreService } from 'src/app/explore/explore.service';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { BookmarksService } from 'src/app/bookmarks/bookmarks.service';
import { StoryType } from 'src/app/story/models/story-type.enum';
import * as _ from 'lodash';

import { sortModels } from 'src/app/bookmarks/models/bookmark-sort.model';
import { bookmarkSortOrders, bookmarkSortTitleAsc } from 'src/app/bookmarks/data/bookmark-sort';

const initialState: GalleryStateModel = {
  loading: true,
  searchMode: GallerySearchMode.Query,
  viewMode: GalleryViewMode.Cards,
  sort: bookmarkSortTitleAsc,
  sortOptions: bookmarkSortOrders,
  studioOptions: [],
  studioQuery: '',
  studioCategoryId: 'category||',
  searchResults: [],
  checkedResults: []
};

@State<GalleryStateModel>({
  name: 'gallery',
  defaults: initialState
})
export class GalleryState {
  /**
   * Selectors
   */
  @Selector()
  static getLoading(state: GalleryStateModel) {
    return state.loading;
  }

  @Selector()
  static getSearchMode(state: GalleryStateModel) {
    return state.searchMode;
  }

  @Selector()
  static getViewMode(state: GalleryStateModel) {
    return state.viewMode;
  }

  @Selector()
  static getSort(state: GalleryStateModel) {
    return state.sort;
  }

  @Selector()
  static getSortOptions(state: GalleryStateModel) {
    return state.sortOptions;
  }

  @Selector()
  static getStudioOptions(state: GalleryStateModel) {
    return state.studioOptions;
  }

  @Selector()
  static getSearchResults(state: GalleryStateModel) {
    const { searchResults, sort } = state;
    const { ascending, fields } = sort;
    return sortModels(searchResults, fields, ascending);
  }

  @Selector()
  static getStudioCategoryId(state: GalleryStateModel) {
    return state.studioCategoryId;
  }

  @Selector()
  static getStudioQuery(state: GalleryStateModel) {
    return state.studioQuery;
  }

  /**
   * Actions
   */
  @Action(GalleryActions.SetSearchMode)
  setSearchMode(context: StateContext<GalleryStateModel>, action: GalleryActions.SetSearchMode) {
    context.patchState({
      searchMode: action.searchMode
    });
  }

  @Action(GalleryActions.SetViewMode)
  setViewMode(context: StateContext<GalleryStateModel>, action: GalleryActions.SetViewMode) {
    context.patchState({
      viewMode: action.viewMode
    });
  }

  @Action(GalleryActions.SetSort)
  setSort(context: StateContext<GalleryStateModel>, action: GalleryActions.SetSort) {
    const searchResults = context.getState().searchResults;

    context.patchState({
      sort: action.sort,
      searchResults: [...searchResults]
    });
  }

  @Action(GalleryActions.GetStudioOptions)
  getStudioCategories(context: StateContext<GalleryStateModel>, { organizationId }: GalleryActions.GetStudioOptions) {
    // Get biodigital categories
    const categories$ = of(exploreMenu.categories); // title, tag

    // Get organization collections
    const collections$: Observable<CollectionModel[]> = organizationId
      ? this.organizationsService.getCollections(organizationId)
      : of([]);

    // Retrieve and combine various options
    forkJoin(
      categories$,
      collections$
    )
      .subscribe(([categories, collections]) => {
        let options: StudioOptionsModel[] = [];

        // Assemble various options
        options = [
          new StudioOptionsModel('My Models', '', 'user'),
          new StudioOptionsModel('Biodigital Models', '', 'category'),
          ...categories.map((category) => {
            return new StudioOptionsModel(category.title, category.tag, 'category');
          })
        ];

        // assemble organization related options
        if (organizationId) {
          options = [
            ...options,
            // add organization level options
            new StudioOptionsModel('Team Models', '', 'organization', organizationId),
            // add collection level options
            ...collections.map((collection: CollectionModel) => {
              return new StudioOptionsModel(collection.collection_name, collection.collection_id.toString(), 'collection', organizationId);
            })
          ];
        }

        context.dispatch(new GalleryActions.GetStudioOptionsSuccess(options));
      });
  }

  @Action(GalleryActions.GetStudioOptionsSuccess)
  getStudioOptionsSuccess(context: StateContext<GalleryStateModel>, action: GalleryActions.GetStudioOptionsSuccess) {
    context.patchState({
      studioOptions: [...action.options]
    });
  }

  @Action(GalleryActions.SearchStudio)
  searchStudio(context: StateContext<GalleryStateModel>, action: GalleryActions.SearchStudio) {
    const { category, query } = action;
    const { organizationId, value, id, type } = category;

    context.patchState({ 
      loading: true,
      studioQuery: query,
      studioCategoryId: id
    });

    let search$ = new Observable<BookmarkModel[]>();

    // Search module services
    switch (type) {
      case 'category':
        search$ = (value === 'public') ?
          this.bookmarksService.getPublicBookmarks(query) :
          this.exploreService.getAnatomyCategory(query, value.toString());
        break;
      case 'organization':
        search$ = this.bookmarksService.getOrganizationContent(organizationId, query);
        break;
      case 'collection':
        search$ = this.organizationsService.getCollectionContent(organizationId, value as number, query);
        break;
      case 'user':
        search$ = this.bookmarksService.getPrivateBookmarks(query);
        break;
    }
    
    search$
      .pipe(
        finalize(() => context.patchState({ loading: false }))
      )
      .subscribe(results => {
        context.dispatch(new GalleryActions.SearchStudioSuccess(results));
      });
  }

  @Action(GalleryActions.SearchStudioSuccess)
  searchStudioSuccess(context: StateContext<GalleryStateModel>, action: GalleryActions.SearchStudioSuccess) {
    context.dispatch(new GalleryActions.SearchSuccess(action.bookmarks));
  }


  @Action(GalleryActions.SearchLoft)
  searchLoft(context: StateContext<GalleryStateModel>, action: GalleryActions.SearchLoft) {
    context.patchState({ loading: true });

    const isExplicit = action.name.indexOf('.json') > 0;

    // Search module services
    this.modulesService.listContents(action.primaryCategory, action.secondaryCategory, (isExplicit) ? action.name : '')
      .pipe(
        map((results) => {
          if (!isExplicit) {
            return results.filter(result => result.name.indexOf(action.name) >= 0);
          } else {
            return results;
          }

        }), 
        finalize(() => context.patchState({ loading: false }))
      )
      .subscribe(
        results => {
          context.dispatch(new GalleryActions.SearchLoftSuccess(results));
        },
        (err) => {
          context.dispatch(new GalleryActions.SearchLoftSuccess([]));
        });
  }

  @Action(GalleryActions.SearchLoftSuccess)
  searchLoftSuccess(context: StateContext<GalleryStateModel>, action: GalleryActions.SearchLoftSuccess) {
    const results = action.bookmarks.map(value => {
      // For loft content, ID and Label both just use filename.
      // For label, strip off the leading directories for easier parsing. 
      const id = value.name
        .replace('/content/modules/', '')
        .replace('.json', '');
      
      const title = value.name
        .split('/')
        .pop()
        .replace('/content/modules', '')
        .replace('.json', '');
      
      const bookmark = new BookmarkModel();
      bookmark.id = id; 
      bookmark.title = title;
      bookmark.type = StoryType.Module;
      return bookmark;
    });

    context.dispatch(new GalleryActions.SearchSuccess(results));
  }

  @Action(GalleryActions.SearchSuccess)
  searchSuccess(context: StateContext<GalleryStateModel>, action: GalleryActions.SearchSuccess) {
    context.patchState({
      searchResults: action.bookmarks
    });
  }

  @Action(GalleryActions.ClearResults)
  clearResults(context: StateContext<GalleryStateModel>, action: GalleryActions.ClearResults) {
    context.patchState({
      searchResults: []
    });
  }

  @Action(GalleryActions.Reset)
  resetGallery(context: StateContext<GalleryStateModel>, action: GalleryActions.Reset) {
    context.patchState(initialState);
  }

  constructor(
    private organizationsService: OrganizationsService,
    private modulesService: ModulesService,
    private exploreService: ExploreService,
    private bookmarksService: BookmarksService
  ) { }
}
